/**
 * Nav link that transparently handles both in-app routes (e.g. `/stock`) and
 * absolute corporate-site URLs (e.g. `https://providecars.co.jp/...`). External
 * http(s) targets render as a real anchor opening in a new tab; in-app paths use
 * the react-router <Link>, which only understands client-side routes.
 *
 * cars-for-sale is a Stock-only deploy, so the shared header (mirrored from the
 * corporate marketing site) mixes in-app Stock routes with absolute links back
 * into providecars.co.jp — this component lets the nav data stay declarative.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isExternalHref } from './chromeNav';

interface ChromeLinkProps {
  to: string;
  className?: string;
  onClick?: () => void;
  'aria-label'?: string;
  children: ReactNode;
}

export function ChromeLink({
  to,
  className,
  onClick,
  children,
  'aria-label': ariaLabel,
}: ChromeLinkProps): JSX.Element {
  if (isExternalHref(to)) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className} onClick={onClick} aria-label={ariaLabel}>
      {children}
    </Link>
  );
}
